import {
  readSavedBundles,
  deleteBundleFromStorage,
  generateShareURL,
  type SavedBundle,
} from '../modules/bundle-state';
import { formatMoney } from '../modules/bundle-variant-popup';
import { addBundleToCart } from '../modules/bundle-cart-add';
import { trackEvent } from '../utils/analytics';

function showToast(msg: string) {
  const el = document.createElement('div');
  el.className = 'bundle-toast';
  el.textContent = msg;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-visible'));
  setTimeout(() => el.remove(), 2800);
}

class BundleSavedSection extends HTMLElement {
  private list: HTMLUListElement | null = null;
  private sectionEl: HTMLElement | null = null;

  connectedCallback() {
    this.list = this.querySelector<HTMLUListElement>('.bundle-list-saved__list');
    this.sectionEl = this.closest<HTMLElement>('.bundle-list-saved');
    this.render();
    this.addEventListener('click', this.handleClick.bind(this));
    // The builder (bundle-tool.ts) deletes a bundle from storage when it's added
    // to the cart — this list has no other way to learn that happened.
    document.addEventListener('bundle:saved-bundles-changed', () => this.render());
  }

  private get storeLang(): string {
    return (window as any).currentLang ?? 'en';
  }

  private get labelIncluded(): string {
    return this.dataset.labelIncluded ?? 'Products included';
  }

  private get labelIncludedHide(): string {
    return this.dataset.labelIncludedHide ?? 'Hide products';
  }

  private get labelAddToCart(): string {
    return this.dataset.labelAddToCart ?? 'Add to cart';
  }

  private get labelAddProduct(): string {
    return this.dataset.labelAddProduct ?? 'Add a product';
  }

  private render() {
    const bundles = readSavedBundles();
    // Breadcrumb entry point to the saved-bundles dialog (see
    // sections/main-bundle-tool.liquid) — nothing to open when there's
    // nothing saved, so it stays hidden alongside the list itself.
    const navButtons = document.querySelectorAll<HTMLElement>('[data-open-saved-bundles]');

    if (bundles.length === 0) {
      if (this.sectionEl) this.sectionEl.style.display = 'none';
      navButtons.forEach((btn) => { btn.hidden = true; });
      return;
    }

    if (this.sectionEl) this.sectionEl.style.display = '';
    navButtons.forEach((btn) => { btn.hidden = false; });

    if (!this.list) return;
    this.list.innerHTML = bundles.map((b) => this.cardHTML(b)).join('');
  }

  // The thumbnail row shows at most this many product slots (matches the
  // Bundle Tool's own tier ceiling — the option list only goes up to Quad
  // today). TODO: replace with a value read from centralized Theme Settings
  // once tier config (min/max/discount) is consolidated out of
  // main-bundle-tool.liquid / bundle-list-types.liquid.
  private static readonly MAX_PRODUCTS = 4;

  private cardHTML(bundle: SavedBundle): string {
    const products = bundle.state.products;
    const modifyURL = `${Shopify.routes.root}pages/bundle?bundle=${encodeURIComponent(
      JSON.stringify({ ...bundle.state, name: bundle.name }),
    )}&bundleId=${encodeURIComponent(bundle.id)}`;

    const isFr = this.storeLang === 'fr';
    const modifyLabel = this.escape(this.dataset.btnModify ?? (isFr ? 'Modifier' : 'Modify'));

    // Thumbnail row: one square per saved product, plus an "add another"
    // slot for every remaining space under the tier ceiling — matches the
    // Figma "My bundle" card (a row of small product squares, not a single
    // full-bleed cover photo). The row always renders exactly MAX_PRODUCTS
    // squares, so the card grid stays visually aligned regardless of how
    // many products are saved.
    const shownProducts = products.slice(0, BundleSavedSection.MAX_PRODUCTS);
    const emptyCount = BundleSavedSection.MAX_PRODUCTS - shownProducts.length;
    const addSlotHTML = `
      <a class="bundle-saved-card__thumb bundle-saved-card__thumb--add" href="${modifyURL}" data-action="modify" aria-label="${this.escape(this.labelAddProduct)}">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </a>
    `;
    const thumbsHTML = `
      <div class="bundle-saved-card__thumbs-row">
        ${shownProducts.map((p) => `
          <div class="bundle-saved-card__thumb">${
            p.image ? `<img src="${this.escape(p.image)}" alt="${this.escape(p.title)}" loading="lazy">` : ''
          }</div>
        `).join('')}
        ${addSlotHTML.repeat(Math.max(emptyCount, 0))}
      </div>
    `;

    const totalCents = products.reduce((sum, p) => sum + p.price, 0);
    const discountedCents = Math.round(totalCents * (1 - (bundle.state.discountPercent ?? 0) / 100));
    const priceHTML = bundle.state.discountPercent
      ? `
        <span class="bundle-saved-card__price">
          <s class="bundle-saved-card__price-original">${formatMoney(totalCents, this.storeLang)}</s>
          <strong class="bundle-saved-card__price-final">${formatMoney(discountedCents, this.storeLang)}</strong>
        </span>
      `
      : `<span class="bundle-saved-card__price">${formatMoney(totalCents, this.storeLang)}</span>`;

    const includedItemsHTML = products.map((p) => `
      <li>${this.escape(p.title)}${p.variantTitle ? ` — ${this.escape(p.variantTitle)}` : ''}</li>
    `).join('');

    return `
      <li class="bundle-saved-card" data-bundle-id="${this.escape(bundle.id)}">
        ${thumbsHTML}
        <div class="bundle-card__body">
          <span class="bundle-saved-card__name">${this.escape(bundle.name)}</span>
          ${priceHTML}
          <div class="bundle-saved-card__included">
            <button type="button" class="bundle-saved-card__included-toggle" aria-expanded="false">
              <span class="bundle-saved-card__included-toggle-text">${this.escape(this.labelIncluded)}</span>
              <svg class="bundle-saved-card__included-chevron" width="10" height="6" viewBox="0 0 14 8" fill="none" aria-hidden="true">
                <path d="M1 1L7 7L13 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <ul class="bundle-saved-card__included-list" hidden>${includedItemsHTML}</ul>
          </div>
          <div class="bundle-saved-card__footer">
            <button type="button" class="bundle-saved-card__add-to-cart button button--fifth" data-action="add-to-cart"${products.length < 2 ? ' disabled' : ''}>
              ${this.escape(this.labelAddToCart)}
            </button>
            <div class="bundle-saved-card__actions">
              <a class="bundle-saved-card__icon-btn" href="${modifyURL}" data-action="modify" aria-label="${modifyLabel}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                </svg>
              </a>
              <button class="bundle-saved-card__icon-btn" data-action="share" aria-label="${isFr ? 'Partager' : 'Share'}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
              </button>
              <button class="bundle-saved-card__icon-btn bundle-saved-card__icon-btn--delete" data-action="delete" aria-label="${isFr ? 'Supprimer' : 'Delete'}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  private handleClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLElement>('[data-action]');
    if (btn) {
      const card = btn.closest<HTMLElement>('[data-bundle-id]');
      if (card) {
        const id = card.dataset.bundleId!;
        const action = btn.dataset.action;

        if (action === 'delete') this.handleDelete(id);
        else if (action === 'share') this.handleShare(id);
        else if (action === 'add-to-cart') this.handleAddToCart(id, btn as HTMLButtonElement);
        else if (action === 'modify') this.handleModify(e, btn as HTMLAnchorElement);
      }
    }

    const toggle = target.closest<HTMLButtonElement>('.bundle-saved-card__included-toggle');
    if (toggle) this.handleToggleIncluded(toggle);
  }

  private handleToggleIncluded(toggle: HTMLButtonElement) {
    const list = toggle.nextElementSibling as HTMLElement | null;
    const label = toggle.querySelector<HTMLElement>('.bundle-saved-card__included-toggle-text');
    if (!list || !label) return;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isExpanded));
    list.hidden = isExpanded;
    label.textContent = isExpanded ? this.labelIncluded : this.labelIncludedHide;
  }

  private async handleAddToCart(id: string, btn: HTMLButtonElement) {
    const bundle = readSavedBundles().find((b) => b.id === id);
    if (!bundle || bundle.state.products.length < 2 || btn.disabled) return;

    btn.disabled = true;
    const added = await addBundleToCart(
      bundle.state.products.map((p) => ({ variantId: p.variantId })),
      {
        discountPercent: bundle.state.discountPercent ?? 0,
        bundleName: bundle.name,
        isFr: this.storeLang === 'fr',
        showToast,
        source: 'bundle-saved-card',
      },
    );
    btn.disabled = false;

    if (added) {
      trackEvent('bundle_add_to_cart', {
        product_count: bundle.state.products.length,
        discount_percent: bundle.state.discountPercent ?? 0,
        source: 'saved-list',
      });
      deleteBundleFromStorage(id);
      this.render();
    }
  }

  private handleDelete(id: string) {
    const deleted = deleteBundleFromStorage(id);
    if (!deleted) {
      showToast(this.storeLang === 'fr' ? 'Impossible de supprimer le bundle' : 'Could not delete bundle');
      return;
    }
    this.render();
  }

  // "My bundles" now lives on the same page as the builder (Task 7) — Modify's
  // navigation would silently discard an unsaved in-progress bundle. Only
  // intervenes when there's actually something to protect; otherwise the
  // native <a href> navigation proceeds untouched.
  private async handleModify(e: MouseEvent, link: HTMLAnchorElement) {
    const guard = window.kaseMeBundleReplaceGuard;
    if (!guard || !guard.hasUnsaved()) return;

    e.preventDefault();
    const decision = await guard.confirm();
    if (decision === 'cancel') return;
    window.location.href = link.href;
  }

  private handleShare(id: string) {
    const bundles = readSavedBundles();
    const bundle = bundles.find((b) => b.id === id);
    if (!bundle) return;

    const url = generateShareURL(bundle);
    const isFr = this.storeLang === 'fr';
    trackEvent('bundle_share', { product_count: bundle.state.products.length });

    navigator.clipboard.writeText(url).then(() => {
      showToast(isFr ? 'Lien copié !' : 'Link copied!');
    }).catch(() => {
      showToast(isFr ? 'Impossible de copier le lien' : 'Could not copy link');
    });
  }

  private escape(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('bundle-saved-section', BundleSavedSection);
